<template>
  <v-row no-gutters class="fill-height flex-column" style="overflow-y: auto; overflow-x: hidden;">
    <v-col cols="12" class="pr-6" v-if="mapPanelDataFetched">
      <card-title-row :straightTitle="$i18n.t('sidebar.measurementsModule.title')" class="pt-5 pb-3" />
      <sidebar-form :inputs="inputs" :is-valid.sync="isValid" ref="form" v-model="params" :is-editing-on="!loading">
        <template #measurementsTxt="scope">
          <v-row no-gutters>
            <v-col cols="auto" class="sidebar-form-name">
              {{ $i18n.t(scope.translationPath) }}
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-col :cols="scope.cols">
              <data-input v-model="params[scope.name]" v-bind="scope" />
            </v-col>
          </v-row>
        </template>
      </sidebar-form>
      <sidebar-row>
        <v-row no-gutters>
          <v-col cols="auto">
            <base-button
              style="padding-right: 24px; padding-left: 24px"
              :translation-path="resultGjson ? 'button.generateAgain' : 'button.generate'"
              :disabled="!isValid"
              :loading="loading"
              @click="getGjson(params, parseResult, true, true)"
            />
          </v-col>
        </v-row>
        <template v-if="resultGjson">
          <v-row no-gutters class="pt-5">
            <v-col cols="auto" style="font-weight: 500; color: rgba(0,0,0,0.5); font-size: 14px">
              {{
                $i18n.t('sidebar.measurementsModule.dataSourceWithMeasurements', {
                  name: this.measurementsDataSourceVerboseName,
                })
              }}
            </v-col>
          </v-row>
          <v-row no-gutters class="pt-5 pb-11">
            <v-col cols="auto">
              <base-button
                style="padding-right: 24px; padding-left: 24px"
                translation-path="button.saveToDataSource"
                @click="saveFeatures(resultGjson)"
              />
            </v-col>
          </v-row>
        </template>
      </sidebar-row>
    </v-col>
    <main-loader v-else />
  </v-row>
</template>
<script>
import { call, get } from 'vuex-pathify';

import CardTitleRow from '@/components/CardTitleRow';
import DataInput from '@/components/DataInput';
import MainLoader from '@/components/MainLoader';
import SidebarForm from '@/components/SidebarForm';
import SidebarRow from '@/components/SidebarRow';

export default {
  name: 'Measurements',
  components: { CardTitleRow, DataInput, MainLoader, SidebarForm, SidebarRow },
  computed: {
    dataSources: get('layers/metadata'),
    mapPanelDataFetched: get('map/mapPanelDataFetched'),
    projectElements: get('layers/project@layers'),
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers;
    },
    measurementsMapping: get('admin/modulesMapping@measurements'),
    measurementsDataSourceName() {
      return this.measurementsMapping?.selected_datasource_name || '';
    },
    measurementsDataSourceVerboseName() {
      return this.dataSources[this.measurementsDataSourceName]?.verbose_name || '';
    },
    xAttributeName() {
      return this.measurementsMapping?.selected_coordinate_x_column || '';
    },
    yAttributeName() {
      return this.measurementsMapping?.selected_coordinate_y_column || '';
    },
    zAttributeName() {
      return this.measurementsMapping?.selected_coordinate_z_column || '';
    },
    descriptionAttributeName() {
      return this.measurementsMapping?.selected_description_column || '';
    },
    coordinatesEpsgs: get('map/coordinatesEpsgs'),
    coordinatesEpsgsArray() {
      return Object.keys(this.coordinatesEpsgs).reduce((total, current) => {
        return [...total, { text: this.coordinatesEpsgs[current].text, value: current }];
      }, []);
    },
    measurementsTxt() {
      return this.params['measurementsTxt'];
    },
    delimiter() {
      return this.params['delimiter'];
    },
    measurementsTxtReady() {
      return this.measurementsTxt && this.delimiter ? true : false;
    },
    inputs() {
      return [
        {
          name: 'measurementsTxt',
          dataType: { name: 'textarea' },
          translationPath: 'sidebar.measurementsModule.measurementsTxt',
          cols: '12',
          hasNameColumn: false,
          rules: [v => !!v || v == '0' || 'rules.required'],
          hideDetails: true,
          disabled: this.loading,
        },
        {
          name: 'delimiter',
          dataType: { name: 'select' },
          items: [
            { text: this.$i18n.t('chars.tabulator'), value: '\t' },
            { text: this.$i18n.t('chars.semicolon'), value: ';' },
            { text: this.$i18n.t('chars.comma'), value: ',' },
            { text: this.$i18n.t('chars.verticalLine'), value: '|' },
            { text: this.$i18n.t('chars.space'), value: ' ' },
          ],
          translationPath: 'sidebar.measurementsModule.delimiter',
          cols: '6',
          nameCols: '6',
          clearable: false,
          rules: [v => !!v || v == '0' || 'rules.required'],
          style: 'min-height: 76px',
        },
        {
          name: 'columnX',
          dataType: { name: 'select' },
          items: this.columns,
          translationPath: 'sidebar.measurementsModule.columnX',
          cols: '6',
          nameCols: '6',
          clearable: false,
          rules: [v => !!v || v == '0' || 'rules.required'],
          style: 'min-height: 76px',
          disabled: !this.measurementsTxtReady,
          disabledSorting: true,
        },
        {
          name: 'columnY',
          dataType: { name: 'select' },
          items: this.columns,
          translationPath: 'sidebar.measurementsModule.columnY',
          cols: '6',
          nameCols: '6',
          clearable: false,
          rules: [v => !!v || v == '0' || 'rules.required'],
          style: 'min-height: 76px',
          disabled: !this.measurementsTxtReady,
          disabledSorting: true,
        },
        {
          name: 'columnZ',
          dataType: { name: 'select' },
          items: this.columns,
          translationPath: 'sidebar.measurementsModule.columnZ',
          cols: '6',
          nameCols: '6',
          clearable: true,
          disabled: !this.measurementsTxtReady,
          disabledSorting: true,
        },
        {
          name: 'columnDescription',
          dataType: { name: 'select' },
          items: this.columns,
          translationPath: 'sidebar.measurementsModule.columnDescription',
          cols: '6',
          nameCols: '6',
          clearable: true,
          disabled: !this.measurementsTxtReady,
          disabledSorting: true,
        },
        {
          name: 'epsg',
          dataType: { name: 'select' },
          items: this.coordinatesEpsgsArray,
          translationPath: 'sidebar.measurementsModule.coordinateSystem',
          cols: '6',
          nameCols: '6',
          clearable: false,
          rules: [v => !!v || v == '0' || 'rules.required'],
        },
      ];
    },
  },
  data: () => ({
    columns: [],
    isValid: false,
    loading: false,
    params: {
      measurementsTxt: '',
      delimiter: '',
      columnX: '',
      columnY: '',
      columnZ: '',
      columnDescription: '',
      epsg: '',
    },
    parseResult: [],
    resultGjson: undefined,
  }),
  methods: {
    uploadDataSource: call('layers/uploadDataSource'),
    getGjson(params = this.params, parseResult = this.parseResult, addToMap = false, zoom = false) {
      this.loading = true;
      const { columnX, columnY, columnZ, columnDescription, epsg } = params;
      const mappedResult = parseResult.map(row => {
        return {
          x: parseFloat(row[columnX].replaceAll(',', '.')) || null,
          y: parseFloat(row[columnY].replaceAll(',', '.')) || null,
          ...(columnZ || columnZ === 0 ? { z: parseFloat(row[columnZ].replaceAll(',', '.')) || null } : {}),
          ...(columnDescription || columnDescription === 0 ? { description: row[columnDescription] || '' } : {}),
        };
      });
      this.resultGjson = {
        type: 'FeatureCollection',
        crs: {
          properties: {
            name: epsg,
          },
          type: 'name',
        },
        features: mappedResult.map(feature => {
          const { x, y, z, description } = feature;
          return {
            type: 'Feature',
            properties: {
              [this.xAttributeName]: x,
              [this.yAttributeName]: y,
              ...(columnZ || columnZ === 0 ? { [this.zAttributeName]: z } : {}),
              ...(columnDescription || columnDescription === 0 ? { [this.descriptionAttributeName]: description } : {}),
            },
            geometry: { type: 'Point', coordinates: [x, y] },
          };
        }),
      };
      if (addToMap) {
        this.$root.$emit('toggleMeasurementsFeatures', this.resultGjson, zoom);
      }
      this.loading = false;
    },
    parseCsv(measurementsTxt = this.measurementsTxt, delimiter = this.delimiter) {
      const result = this.$papa.parse(measurementsTxt, { delimiter: delimiter });
      this.columns = result.data[0].map((e, idx) => {
        return { text: `${this.$i18n.t('sidebar.measurementsModule.column')} ${idx + 1}`, value: idx };
      });
      this.parseResult = result.data;
    },
    async saveFeatures(features = this.resultGjson) {
      try {
        this.loading = true;
        const blob = new Blob([JSON.stringify(features)], { type: 'application/geo+json;charset=utf-8;' });
        const file = new File([blob], 'measurements.geojson');
        const formData = new FormData();
        formData.append('geojson', file);
        formData.append('srid', features.crs.properties.name.split(':')[1]);
        formData.append('datasource', this.measurementsDataSourceName);
        await this.uploadDataSource(formData);
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('sidebar.measurementsModule.successSaveFeatures'),
        });
        this.projectLayers.forEach(layer => {
          if (layer.data_source_name === this.measurementsDataSourceName) {
            this.$root.$emit('refreshMvtSource', layer.id);
          }
        });
        this.loading = false;
      } catch {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.$watch(
      vm => {
        const { measurementsTxt, delimiter } = vm;
        return { measurementsTxt, delimiter };
      },
      nV => {
        this.$set(this.params, 'columnX', '');
        this.$set(this.params, 'columnY', '');
        this.$set(this.params, 'columnZ', '');
        this.$set(this.params, 'columnDescription', '');
        this.$refs['form']?.$refs?.form.resetValidation();
        const { measurementsTxt, delimiter } = nV;
        if (measurementsTxt && delimiter) {
          this.parseCsv(measurementsTxt, delimiter);
        } else {
          this.columns = [];
          this.parseResult = [];
        }
      },
      {
        immediate: true,
        deep: true,
      }
    );
  },
  beforeDestroy() {
    this.$root.$emit('toggleMeasurementsFeatures');
  },
};
</script>
<style lang="scss" scoped>
.sidebar-form-name {
  color: #202124;
  float: left;
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  padding-bottom: 2px !important;
  text-align: left;
}
</style>
